import { useState } from "react";
import { FilePdfIcon, PrinterIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";

/**
 * Triggers a PDF download by hitting the authed PDF endpoint and saving the blob.
 * Falls back to opening in a new tab if the download fails.
 *
 * Optional `tokenUrl` prop is used by the public report page where there is no
 * cookie/session auth available — instead we build a direct link to the public
 * /pdf endpoint, which works without auth.
 */
export default function ReportPdfButton({ workOrderId, tokenUrl, label = "Download PDF" }) {
  const [busy, setBusy] = useState(false);

  const downloadAuthed = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(
        `/work-orders/${workOrderId}/diagnostic-report/pdf`,
        { responseType: "blob" }
      );
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `diagnostic-report-${workOrderId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("PDF generation failed — please try again.");
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  if (tokenUrl) {
    return (
      <a
        href={tokenUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="report-pdf-button"
        className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
      >
        <FilePdfIcon size={14} weight="duotone" />
        {label}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={downloadAuthed}
      disabled={busy}
      data-testid="report-pdf-button"
      className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 transition-colors"
    >
      <FilePdfIcon size={14} weight="duotone" />
      {busy ? "Generating…" : label}
    </button>
  );
}

export function PrintButton({ workOrderId, tokenUrl }) {
  const path = tokenUrl
    ? tokenUrl.replace(/\/pdf$/, "/render")
    : `${API}/work-orders/${workOrderId}/diagnostic-report/render`;

  const openPrintWindow = async () => {
    if (tokenUrl) {
      const w = window.open(path, "_blank", "noopener,noreferrer");
      if (w) {
        setTimeout(() => {
          try {
            w.print();
          } catch {
            /* ignore */
          }
        }, 800);
      }
      return;
    }
    // Authed render — fetch with credentials, blob it, open
    try {
      const res = await fetch(path, { credentials: "include" });
      if (!res.ok) throw new Error(`render returned ${res.status}`);
      const html = await res.text();
      const w = window.open("", "_blank", "noopener,noreferrer");
      if (!w) {
        toast.error("Pop-up blocked — allow pop-ups to print.");
        return;
      }
      w.document.write(html);
      w.document.close();
      setTimeout(() => {
        try {
          w.focus();
          w.print();
        } catch {
          /* ignore */
        }
      }, 600);
    } catch (e) {
      toast.error("Print preview failed.");
      console.error(e);
    }
  };

  return (
    <button
      type="button"
      onClick={openPrintWindow}
      data-testid="report-print-button"
      className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
    >
      <PrinterIcon size={14} weight="duotone" />
      Print
    </button>
  );
}
